import { useEffect, useSyncExternalStore } from "react";

import {
  getPreferenceSnapshot,
  initTheme,
  resolvePreference,
  setPreference,
  subscribeTheme,
  type ResolvedTheme,
  type ThemePreference,
} from "./theme";

/**
 * The theme preference and the theme it currently resolves to.
 *
 * `resolved` is what the page is actually showing, so components that need to
 * branch on it (the print preview, an icon) do not have to re-read matchMedia.
 */
export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
} {
  // The server snapshot is annotated, not just inferred: a bare "system" widens
  // to `string` and useSyncExternalStore then infers `string` for the whole hook.
  const serverSnapshot = (): ThemePreference => "system";
  const preference = useSyncExternalStore(subscribeTheme, getPreferenceSnapshot, serverSnapshot);
  return { preference, resolved: resolvePreference(preference), setPreference };
}

/** Mounted once by the app shell: applies the stored preference and keeps "system" live. */
export function useThemeEffect(): void {
  useEffect(() => initTheme(), []);
}
